export const aiAssistant = {
  extends: 'Page',
  flow: 'x',
  minHeight: '100vh',
  background: 'surface',

  Sidebar: {},

  MainContent: {
    flow: 'y',
    flex: '1',
    padding: 'B C',
    gap: 'B',
    overflow: 'auto',
    '@tabletS': { padding: 'A B' },
    '@mobileL': { padding: 'A' },

    PageHeader: {
      flow: 'x',
      align: 'center space-between',
      '@mobileL': { flow: 'y', gap: 'A' },
      HeaderTitle: { tag: 'h1', text: '{{ aiTitle | polyglot }}', fontSize: 'C', fontWeight: '800', color: 'text', '@mobileL': { fontSize: 'B' } },
      HeaderRight: {
        flow: 'x', gap: 'A', align: 'center',
        '@mobileL': { flexWrap: 'wrap' },
        GeminiBadge: { tag: 'span', text: '{{ aiPowered | polyglot }}', fontSize: 'Z', color: 'green', fontWeight: '500', padding: 'Y Z', background: 'greenBg', border: '1px solid greenBorder', borderRadius: 'Z' },
        ThemeToggle: {},
      },
    },

    // Tabs
    TabsRow: {
      flow: 'x',
      gap: 'Z',
      borderBottom: '1px solid surfaceBorderStrong',
      padding: '0 0 Z 0',
      '@mobileL': { flexWrap: 'wrap' },
      TabDraft: {
        extends: 'Button', text: '{{ aiDraftDocs | polyglot }}', padding: 'Z A', fontSize: 'Z', cursor: 'pointer', borderRadius: 'Z',
        background: (el, s) => s.root.aiTab === 'draft' ? 'green' : 'transparent',
        color: (el, s) => s.root.aiTab === 'draft' ? 'black' : 'textSoft',
        fontWeight: (el, s) => s.root.aiTab === 'draft' ? '600' : '400',
        onClick: (e, el, s) => s.root.update({ aiTab: 'draft' }),
      },
      TabAnalysis: {
        extends: 'Button', text: '{{ aiContractAnalysis | polyglot }}', padding: 'Z A', fontSize: 'Z', cursor: 'pointer', borderRadius: 'Z',
        background: (el, s) => s.root.aiTab === 'analysis' ? 'green' : 'transparent',
        color: (el, s) => s.root.aiTab === 'analysis' ? 'black' : 'textSoft',
        fontWeight: (el, s) => s.root.aiTab === 'analysis' ? '600' : '400',
        onClick: (e, el, s) => s.root.update({ aiTab: 'analysis' }),
      },
      TabResearch: {
        extends: 'Button', text: '{{ aiLegalResearch | polyglot }}', padding: 'Z A', fontSize: 'Z', cursor: 'pointer', borderRadius: 'Z',
        background: (el, s) => s.root.aiTab === 'research' ? 'green' : 'transparent',
        color: (el, s) => s.root.aiTab === 'research' ? 'black' : 'textSoft',
        fontWeight: (el, s) => s.root.aiTab === 'research' ? '600' : '400',
        onClick: (e, el, s) => s.root.update({ aiTab: 'research' }),
      },
    },

    // Tab 1 — Document drafting (link to /documents)
    DraftView: {
      flow: 'y', gap: 'A', show: (el, s) => s.root.aiTab === 'draft',
      DraftInfo: { text: '{{ aiDraftAvailable | polyglot }}', fontSize: 'A', color: 'textSoft' },
      GoToDocsLink: { extends: 'Link', href: '/documents', text: '{{ aiGoToDocuments | polyglot }}', fontSize: 'A', color: 'green', textDecoration: 'none' },
    },

    // Tab 2 — Contract analysis
    AnalysisView: {
      flow: 'y', gap: 'A', show: (el, s) => s.root.aiTab === 'analysis',

      AnalysisInput: {
        tag: 'textarea',
        name: 'contract',
        placeholder: '{{ aiPasteContract | polyglot }}',
        padding: 'A',
        fontSize: 'A',
        background: 'surfaceCard',
        border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z',
        color: 'text',
        minHeight: 'E',
        lineHeight: '1.6',
      },
      AnalyzeBtn: {
        extends: 'Button',
        text: (el, s) => s.root.analysisOutput ? '{{ aiReanalyze | polyglot }}' : '{{ aiAnalyze | polyglot }}',
        theme: 'primary',
        padding: 'Z B',
        fontSize: 'A',
        borderRadius: 'Z',
        cursor: 'pointer',
        onClick: (e, el, s) => s.root.update({
          analysisOutput: {
            parties: '{{ aiPartiesData | polyglot }}',
            obligations: '{{ aiObligationsData | polyglot }}',
            risks: '{{ aiRisksData | polyglot }}',
            deadlines: '{{ aiDeadlinesData | polyglot }}',
            recommendations: '{{ aiRecommendationsData | polyglot }}',
          },
        }),
      },

      AnalysisResult: {
        flow: 'y', gap: 'A',
        if: (el, s) => s.root.analysisOutput,

        PartiesCard: {
          flow: 'y', gap: 'Y', padding: 'A', background: 'surfaceCard', border: '1px solid surfaceBorder', borderRadius: 'Z',
          CardLabel: { text: '{{ aiParties | polyglot }}', fontSize: 'Z', fontWeight: '600', color: 'textMuted' },
          CardContent: { text: (el, s) => s.root.analysisOutput ? s.root.analysisOutput.parties : '', fontSize: 'A', color: 'text', whiteSpace: 'pre-wrap', lineHeight: '1.6' },
        },
        ObligationsCard: {
          flow: 'y', gap: 'Y', padding: 'A', background: 'surfaceCard', border: '1px solid surfaceBorder', borderRadius: 'Z',
          CardLabel: { text: '{{ aiObligations | polyglot }}', fontSize: 'Z', fontWeight: '600', color: 'textMuted' },
          CardContent: { text: (el, s) => s.root.analysisOutput ? s.root.analysisOutput.obligations : '', fontSize: 'A', color: 'text', whiteSpace: 'pre-wrap', lineHeight: '1.6' },
        },
        RisksCard: {
          flow: 'y', gap: 'Y', padding: 'A', background: 'redBg', border: '1px solid redBorder', borderRadius: 'Z',
          CardLabel: { text: '{{ aiRiskFactors | polyglot }}', fontSize: 'Z', fontWeight: '600', color: 'red' },
          CardContent: { text: (el, s) => s.root.analysisOutput ? s.root.analysisOutput.risks : '', fontSize: 'A', color: 'text', whiteSpace: 'pre-wrap', lineHeight: '1.6' },
        },
        DeadlinesCard: {
          flow: 'y', gap: 'Y', padding: 'A', background: 'surfaceCard', border: '1px solid surfaceBorder', borderRadius: 'Z',
          CardLabel: { text: '{{ aiDeadlines | polyglot }}', fontSize: 'Z', fontWeight: '600', color: 'textMuted' },
          CardContent: { text: (el, s) => s.root.analysisOutput ? s.root.analysisOutput.deadlines : '', fontSize: 'A', color: 'text', whiteSpace: 'pre-wrap', lineHeight: '1.6' },
        },
        RecommendationsCard: {
          flow: 'y', gap: 'Y', padding: 'A', background: 'greenBg', border: '1px solid greenBorder', borderRadius: 'Z',
          CardLabel: { text: '{{ aiRecommendations | polyglot }}', fontSize: 'Z', fontWeight: '600', color: 'green' },
          CardContent: { text: (el, s) => s.root.analysisOutput ? s.root.analysisOutput.recommendations : '', fontSize: 'A', color: 'text', whiteSpace: 'pre-wrap', lineHeight: '1.6' },
        },
      },
    },

    // Tab 3 — Legal research
    ResearchView: {
      flow: 'y', gap: 'A', flex: '1', show: (el, s) => s.root.aiTab === 'research',

      ResearchTitle: { text: '{{ aiAskQuestion | polyglot }}', fontSize: 'A', color: 'textSoft' },

      ExampleChips: {
        flow: 'x', gap: 'Z', flexWrap: 'wrap',
        Chip1: { extends: 'Button', text: '{{ aiChip1 | polyglot }}', theme: 'ghost', padding: 'Z A', fontSize: 'Z', borderRadius: 'Z', cursor: 'pointer', onClick: (e, el, s) => s.root.update({ researchInput: '{{ aiChip1Question | polyglot }}' }) },
        Chip2: { extends: 'Button', text: '{{ aiChip2 | polyglot }}', theme: 'ghost', padding: 'Z A', fontSize: 'Z', borderRadius: 'Z', cursor: 'pointer', onClick: (e, el, s) => s.root.update({ researchInput: '{{ aiChip2Question | polyglot }}' }) },
        Chip3: { extends: 'Button', text: '{{ aiChip3 | polyglot }}', theme: 'ghost', padding: 'Z A', fontSize: 'Z', borderRadius: 'Z', cursor: 'pointer', onClick: (e, el, s) => s.root.update({ researchInput: '{{ aiChip3Question | polyglot }}' }) },
      },

      ChatArea: {
        flow: 'y',
        gap: 'Z',
        flex: '1',
        padding: 'A',
        background: 'surfaceCard',
        border: '1px solid surfaceBorder',
        borderRadius: 'Z',
        minHeight: 'F',

        EmptyChat: {
          flow: 'y', align: 'center center', flex: '1', gap: 'Z',
          show: (el, s) => !s.root.researchMessages || s.root.researchMessages.length === 0,
          EmptyChatIcon: { extends: 'Icon', icon: 'ai', color: 'textMuted', width: 'C', height: 'C' },
          EmptyChatText: { text: '{{ aiLegalResearchTitle | polyglot }}', fontSize: 'A', color: 'textMuted' },
          EmptyChatSub: { text: '{{ aiEmptyChatSub | polyglot }}', fontSize: 'Z', color: 'textMuted' },
        },
      },

      InputRow: {
        flow: 'x', gap: 'Z',
        ResearchInput: {
          tag: 'input',
          type: 'text',
          name: 'research',
          placeholder: '{{ aiAskPlaceholder | polyglot }}',
          padding: 'Z A',
          fontSize: 'A',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          color: 'text',
          flex: '1',
          value: (el, s) => s.root.researchInput || '',
          onInput: (e, el, s) => s.root.update({ researchInput: e.target.value }),
        },
        SendBtn: {
          extends: 'Button',
          text: '{{ aiSend | polyglot }}',
          theme: 'primary',
          padding: 'Z A',
          fontSize: 'A',
          borderRadius: 'Z',
          cursor: 'pointer',
        },
      },
    },
  },
}
